import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import './AuthPage.css';

const ResetPasswordPage = () => {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl || tokenFromUrl.length < 32) {
      setTokenInvalid(true);
    }
  }, [tokenFromUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = 'الرجاء إدخال كلمة المرور الجديدة';
    } else if (formData.password.length < 8) {
      newErrors.password = 'يجب أن تكون كلمة المرور 8 أحرف على الأقل';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'الرجاء تأكيد كلمة المرور';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'كلمة المرور وتأكيدها غير متطابقين';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm() || !tokenFromUrl) return;

    setIsLoading(true);
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.RESET_PASSWORD), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: tokenFromUrl,
          password: formData.password,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية.');
      }

      setSubmitSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setSubmitError(err.message || 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية.');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenInvalid) {
    return (
      <Layout>
        <div className={`auth-page ${isRTL ? 'rtl' : ''}`}>
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">رابط غير صالح</h1>
              <p className="auth-subtitle">
                رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية. يرجى طلب رابط جديد.
              </p>
            </div>
            <div className="auth-form-container">
              <Link to="/forgot-password" className="auth-link-button">
                طلب رابط جديد
              </Link>
              <p className="auth-footer-text" style={{ marginTop: '1rem' }}>
                <Link to="/login" className="auth-link-button">
                  العودة لتسجيل الدخول
                </Link>
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`auth-page ${isRTL ? 'rtl' : ''}`}>
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">تعيين كلمة مرور جديدة</h1>
            <p className="auth-subtitle">
              أدخل كلمة المرور الجديدة وتأكيدها
            </p>
          </div>

          <div className="auth-form-container">
            {submitError && (
              <div className="auth-error-box">{submitError}</div>
            )}
            {submitSuccess && (
              <div className="auth-error-box" style={{ background: '#ecfdf3', borderColor: '#bbf7d0', color: '#166534' }}>
                تم تحديث كلمة المرور بنجاح. جاري تحويلك لتسجيل الدخول...
              </div>
            )}

            {!submitSuccess && (
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-input-group">
                  <label htmlFor="password" className="auth-label">
                    كلمة المرور الجديدة *
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  {errors.password && <div className="auth-error">{errors.password}</div>}
                </div>
                <div className="auth-input-group">
                  <label htmlFor="confirmPassword" className="auth-label">
                    تأكيد كلمة المرور *
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    className={`auth-input ${errors.confirmPassword ? 'auth-input-error' : ''}`}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && <div className="auth-error">{errors.confirmPassword}</div>}
                </div>
                <button type="submit" className="auth-button" disabled={isLoading}>
                  {isLoading ? 'جاري الحفظ...' : 'حفظ كلمة المرور'}
                </button>
              </form>
            )}

            <div className="auth-footer" style={{ marginTop: '1.5rem' }}>
              <p className="auth-footer-text">
                <Link to="/login" className="auth-link-button">
                  العودة لتسجيل الدخول
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResetPasswordPage;
